// DPad.tsx
import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  useTheme,
  SxProps,
} from "@mui/material";

export interface BoundingBox {
  minRow: number;
  maxRow: number;
  minCol: number;
  maxCol: number;
}

interface DPadProps {
  boundingBox: BoundingBox;
  setBoundingBox: (
    newMinR: number,
    newMaxR: number,
    newMinC: number,
    newMaxC: number
  ) => void;
}

const DPad: React.FC<DPadProps> = ({ boundingBox, setBoundingBox }) => {
  const { minRow, maxRow, minCol, maxCol } = boundingBox;
  const theme = useTheme();

  // Fixed desktop sizes.
  const containerSize = 225;
  const borderWidth = 2;
  const innerGridSize = containerSize - 2 * borderWidth;
  const cellSize = innerGridSize / 5;

  const buttonSize = 32; // plus/minus buttons
  const handleSize = 60;  // draggable handle

  // Font sizes.
  const buttonFontSize = "1rem";
  const labelFontSize = "1rem";
  const arrowFontSize = "1.5rem";
  const handleFontSize = "1.5rem";

  // Initial position.
  const [position, setPosition] = useState<{ x: number; y: number }>({
    x: 266,
    y: window.innerHeight - containerSize - 20,
  });
  const dragStartPos = useRef<{ x: number; y: number } | null>(null);

  // Drag event handlers.
  const startDrag = (clientX: number, clientY: number) => {
    dragStartPos.current = { x: clientX - position.x, y: clientY - position.y };
  };

  const moveDrag = (clientX: number, clientY: number) => {
    if (dragStartPos.current) {
      const newX = clientX - dragStartPos.current.x;
      const newY = clientY - dragStartPos.current.y;
      setPosition({ x: newX, y: newY });
    }
  };

  const endDrag = () => {
    dragStartPos.current = null;
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    startDrag(e.clientX, e.clientY);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    moveDrag(e.clientX, e.clientY);
  };

  const handleMouseUp = () => {
    endDrag();
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (e.touches.length > 0) {
      startDrag(e.touches[0].clientX, e.touches[0].clientY);
      window.addEventListener("touchmove", handleTouchMove, { passive: false });
      window.addEventListener("touchend", handleTouchEnd);
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    e.preventDefault();
    if (e.touches.length > 0) {
      moveDrag(e.touches[0].clientX, e.touches[0].clientY);
    }
  };

  const handleTouchEnd = () => {
    endDrag();
    window.removeEventListener("touchmove", handleTouchMove);
    window.removeEventListener("touchend", handleTouchEnd);
  };

  // Prevent removal of (0,0).
  const updateMap = (
    newMinR: number,
    newMaxR: number,
    newMinC: number,
    newMaxC: number
  ) => {
    if (newMinR > 0 || newMaxR < 0) return;
    if (newMinC > 0 || newMaxC < 0) return;
    setBoundingBox(newMinR, newMaxR, newMinC, newMaxC);
  };

  const buttonSx: SxProps = {
    width: buttonSize,
    height: buttonSize,
    minWidth: buttonSize,
    padding: 0,
    fontSize: buttonFontSize,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": { backgroundColor: theme.palette.primary.dark || "#E04540" },
  };

  const formatLabel = (value: number) =>
    value >= 0 ? `+${value}` : `-${Math.abs(value)}`;

  // Simple Cell component.
  const Cell: React.FC<{ children?: React.ReactNode; sx?: SxProps }> = ({
    children,
    sx,
  }) => (
    <Box
      sx={{
        width: cellSize,
        height: cellSize,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        m: 0,
        p: 0,
        ...sx,
      }}
    >
      {children}
    </Box>
  );

  return (
    <Box
      sx={{
        position: "absolute",
        left: position.x,
        top: position.y,
        width: containerSize,
        height: containerSize,
        boxSizing: "border-box",
        userSelect: "none",
        m: 0,
        p: 0,
        zIndex: 2,
      }}
    >
      {/* Grid Container with translucent backdrop */}
      <Box
        sx={{
          width: "100%",
          height: "100%",
          border: `${borderWidth}px solid ${theme.palette.primary.main}`,
          borderRadius: 8,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(5px)",
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gridTemplateRows: "repeat(5, 1fr)",
          gap: 0,
          position: "relative",
          boxSizing: "border-box",
          m: 0,
          p: 0,
          overflow: "hidden",
        }}
      >
        {/*** Row 1 – Up Controls ***/}
        {/* Cell 1: (empty) */}
        <Cell />
        {/* Cell 2: now "-" (shrink up) */}
        <Cell>
          <Button
            variant="contained"
            sx={buttonSx}
            disabled={minRow === 0}
            onClick={() => updateMap(minRow + 1, maxRow, minCol, maxCol)}
          >
            –
          </Button>
        </Cell>
        {/* Cell 3: Up label */}
        <Cell>
          <Box sx={{ textAlign: "center", lineHeight: 1, m: 0, p: 0 }}>
            <Typography
              sx={{ fontSize: arrowFontSize, m: 0, color: theme.palette.text.primary }}
            >
              ⬆
            </Typography>
            <Typography
              sx={{ fontSize: labelFontSize, m: 0, color: theme.palette.text.primary }}
            >
              {formatLabel(minRow)}
            </Typography>
          </Box>
        </Cell>
        {/* Cell 4: now "+" (expand up) */}
        <Cell>
          <Button
            variant="contained"
            sx={buttonSx}
            onClick={() => updateMap(minRow - 1, maxRow, minCol, maxCol)}
          >
            +
          </Button>
        </Cell>
        {/* Cell 5: (empty) */}
        <Cell />

        {/*** Row 2 – Left/Right Controls (Top Row) ***/}
        {/* Cell 1: left side, now "+" (expand left) */}
        <Cell>
          <Button
            variant="contained"
            sx={buttonSx}
            onClick={() => updateMap(minRow, maxRow, minCol - 1, maxCol)}
          >
            +
          </Button>
        </Cell>
        {/* Cells 2-4: empty */}
        <Cell />
        <Cell />
        <Cell />
        {/* Cell 5: right side, now "+" (expand right) */}
        <Cell>
          <Button
            variant="contained"
            sx={buttonSx}
            onClick={() => updateMap(minRow, maxRow, minCol, maxCol + 1)}
          >
            +
          </Button>
        </Cell>

        {/*** Row 3 – Left/Right Labels ***/}
        {/* Cell 1: left label */}
        <Cell sx={{ flexDirection: "row", m: 0, p: 0 }}>
          <Typography
            sx={{ fontSize: arrowFontSize, m: 0, color: theme.palette.text.primary }}
          >
            ⬅
          </Typography>
          <Typography
            sx={{ fontSize: labelFontSize, m: 0, color: theme.palette.text.primary }}
          >
            {formatLabel(minCol)}
          </Typography>
        </Cell>
        {/* Cells 2 and 3: empty */}
        <Cell />
        <Cell />
        {/* Cell 4: empty */}
        <Cell />
        {/* Cell 5: right label */}
        <Cell sx={{ flexDirection: "row", m: 0, p: 0 }}>
          <Typography
            sx={{ fontSize: labelFontSize, m: 0, color: theme.palette.text.primary }}
          >
            {formatLabel(maxCol)}
          </Typography>
          <Typography
            sx={{ fontSize: arrowFontSize, m: 0, color: theme.palette.text.primary }}
          >
            ⮕
          </Typography>
        </Cell>

        {/*** Row 4 – Left/Right Controls (Bottom Row) ***/}
        {/* Cell 1: left side, now "-" (shrink left) */}
        <Cell>
          <Button
            variant="contained"
            sx={buttonSx}
            disabled={minCol === 0}
            onClick={() => updateMap(minRow, maxRow, minCol + 1, maxCol)}
          >
            –
          </Button>
        </Cell>
        {/* Cells 2-4: empty */}
        <Cell />
        <Cell />
        <Cell />
        {/* Cell 5: right side, now "-" (shrink right) */}
        <Cell>
          <Button
            variant="contained"
            sx={buttonSx}
            disabled={maxCol === 0}
            onClick={() => updateMap(minRow, maxRow, minCol, maxCol - 1)}
          >
            –
          </Button>
        </Cell>

        {/*** Row 5 – Down Controls ***/}
        {/* Cell 1: (empty) */}
        <Cell />
        {/* Cell 2: now "-" (shrink down) */}
        <Cell>
          <Button
            variant="contained"
            sx={buttonSx}
            disabled={maxRow === 0}
            onClick={() => updateMap(minRow, maxRow - 1, minCol, maxCol)}
          >
            –
          </Button>
        </Cell>
        {/* Cell 3: Down label */}
        <Cell>
          <Box sx={{ textAlign: "center", lineHeight: 1, m: 0, p: 0 }}>
            <Typography
              sx={{ fontSize: labelFontSize, m: 0, color: theme.palette.text.primary }}
            >
              {formatLabel(maxRow)}
            </Typography>
            <Typography
              sx={{ fontSize: arrowFontSize, m: 0, color: theme.palette.text.primary }}
            >
              ⬇
            </Typography>
          </Box>
        </Cell>
        {/* Cell 4: now "+" (expand down) */}
        <Cell>
          <Button
            variant="contained"
            sx={buttonSx}
            onClick={() => updateMap(minRow, maxRow + 1, minCol, maxCol)}
          >
            +
          </Button>
        </Cell>
        {/* Cell 5: (empty) */}
        <Cell />
      </Box>

      {/* Draggable Handle (centered) */}
      <Box
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        sx={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: handleSize,
          height: handleSize,
          borderRadius: "50%",
          backgroundColor: theme.palette.primary.contrastText,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "move",
          zIndex: 10,
          boxSizing: "border-box",
          m: 0,
          p: 0,
        }}
      >
        <Typography
          sx={{ color: theme.palette.primary.main, fontSize: handleFontSize, m: 0 }}
        >
          ⠿
        </Typography>
      </Box>
    </Box>
  );
};

export default DPad;
